/**
 * Pure, side-effect-free decision helpers backing the order-refresh reliability
 * logic in {@link ./OrdersContext.js}. They are extracted so the rules that keep
 * orders flowing — and that previously, when broken, forced users to restart the
 * app to see new/pending orders — can be unit-tested in isolation (Node/Jest)
 * without a React-Native runtime.
 */

/**
 * Decide whether a fresh order sync may start.
 *
 * The order sync uses a single in-flight lock. The danger is a lock that never
 * releases (a hung request, a timer that never fired) silently wedging all
 * future refreshes until an app restart. This guard lets a new attempt through
 * once an existing lock is older than the watchdog window, so the system always
 * self-heals.
 *
 * @param {boolean} isLocked - Whether a sync is currently marked in-flight.
 * @param {number} lockStartedAt - epoch ms when the in-flight sync began (0 if none).
 * @param {number} now - current epoch ms.
 * @param {number} watchdogMs - max plausible age of an in-flight sync.
 * @returns {boolean} true if a new sync should be allowed to start.
 */
export const shouldAllowSync = (isLocked, lockStartedAt, now, watchdogMs) => {
  if (!isLocked) {
    return true;
  }
  const lockAgeMs = now - (lockStartedAt || 0);
  return lockAgeMs >= watchdogMs;
};

/**
 * True when an AppState transition represents the app returning to the
 * foreground (background/inactive -> active). Used to trigger an immediate
 * refresh on resume, because the OS suspends JS timers while backgrounded on
 * both iOS and Android.
 *
 * @param {string} prevState - previous AppState value.
 * @param {string} nextState - next AppState value.
 * @returns {boolean}
 */
/**
 * Whether a polling tick can be skipped because a fetch already succeeded
 * recently (realtime event, focus refresh). Skips only while the last success
 * is younger than 80% of the interval, so data is still never older than about
 * one interval plus the fetch time.
 *
 * @param {number} lastSuccessAt Epoch ms of the last successful fetch (0 = never).
 * @param {number} now Current epoch ms.
 * @param {number} intervalMs Polling interval.
 * @returns {boolean}
 */
export const shouldSkipPollAfterRecentFetch = (lastSuccessAt, now, intervalMs) =>
    Number.isFinite(lastSuccessAt) && lastSuccessAt > 0 && now - lastSuccessAt < intervalMs * 0.8;

export const cameToForeground = (prevState, nextState) =>
  (prevState === 'inactive' || prevState === 'background') && nextState === 'active';

/**
 * Merge a fresh server order list with locally-created orders that have not yet
 * round-tripped to the server, so an early/poll fetch can never drop an
 * optimistic `pending_sync` order (which would flash a table back to "free" and
 * reopen the duplicate-order window).
 *
 * @param {Array<object>} serverOrders - orders returned by the API.
 * @param {Array<object>} currentOrders - current in-memory orders.
 * @returns {Array<object>} merged list with still-pending local orders preserved up front.
 */
export const mergePendingOrders = (serverOrders, currentOrders) => {
  const safeServer = Array.isArray(serverOrders) ? serverOrders : [];
  const safeCurrent = Array.isArray(currentOrders) ? currentOrders : [];
  const serverOrderIds = new Set(safeServer.map((o) => o && o._id));
  const stillPendingLocalOrders = safeCurrent.filter(
    (o) => o && o.status === 'pending_sync' && !serverOrderIds.has(o._id)
  );
  return stillPendingLocalOrders.length
    ? [...stillPendingLocalOrders, ...safeServer]
    : safeServer;
};

/**
 * Statuses that represent a finished order. A finished order may legitimately
 * fall out of the server's narrow "today / this place" window over time, so we
 * let it age out. An order in ANY other state is "live" and must never silently
 * disappear from the operator's view.
 */
const TERMINAL_STATUSES = new Set(['closed', 'cancelled', 'canceled', 'deleted']);

/**
 * Business-day boundary, kept in sync with the backend order endpoint
 * (`ORDER_LIST_BUSINESS_DAY_START_HOUR`/`_MINUTE` in restaurant.controller.js):
 * a shift/day starts at 05:30 local time and lasts 24h. A request without
 * explicit dates returns only the current business day, so the client must use
 * the same cut-off when deciding which retained local orders still belong to it.
 */
const BUSINESS_DAY_START_HOUR = 5;
const BUSINESS_DAY_START_MINUTE = 30;

/**
 * Start of the business day that contains `now`. If `now` is before the daily
 * cut-off we are still inside the previous business day. Computed in LOCAL time
 * to match the backend (which uses the server's local-time date constructor); on
 * a device in the venue's timezone the two windows line up.
 *
 * @param {Date|number} [now] - reference instant (defaults to current time).
 * @returns {Date} start of the current business day.
 */
export const getBusinessDayStart = (now = new Date()) => {
  const ref = now instanceof Date ? now : new Date(now);
  const start = new Date(
    ref.getFullYear(), ref.getMonth(), ref.getDate(),
    BUSINESS_DAY_START_HOUR, BUSINESS_DAY_START_MINUTE, 0, 0
  );
  if (ref.getTime() < start.getTime()) {
    start.setDate(start.getDate() - 1);
  }
  return start;
};

/**
 * Whether a live local order the server window did NOT return still plausibly
 * belongs to the current business day, mirroring the server's today-only window.
 *
 * This is the bound that stops yesterday's orders from being resurrected. The
 * server's order endpoint returns only the current business day, so a previous-
 * day order that is still "open" in the local cache can NEVER come back from the
 * server to be aged out — without this bound {@link reconcileOrderLists} kept
 * re-adding it on every launch, so the operator saw yesterday's orders instead
 * of today's.
 *
 * We only DROP an order we can positively prove is stale: it has a `createdAt`
 * before today's window AND no current/future `preOrder`. Anything we cannot
 * date (no `createdAt`) is KEPT, so the "never lose an order" guarantee holds for
 * any genuinely live order we can't prove is old; an upcoming pre-order is kept
 * regardless of when it was created.
 *
 * @param {object} order
 * @param {Date|number} businessDayStart - start of the current business day.
 * @returns {boolean} true if the order should be retained as current.
 */
export const isWithinCurrentBusinessDay = (order, businessDayStart) => {
  if (!order) {
    return false;
  }
  const startMs = businessDayStart instanceof Date
    ? businessDayStart.getTime()
    : Number(businessDayStart);
  if (!Number.isFinite(startMs)) {
    return true; // no usable boundary -> cannot prove stale -> keep
  }

  const preOrderMs = order.preOrder ? new Date(order.preOrder).getTime() : NaN;
  if (Number.isFinite(preOrderMs) && preOrderMs >= startMs) {
    return true; // scheduled for today or later
  }

  const createdMs = order.createdAt ? new Date(order.createdAt).getTime() : NaN;
  if (!Number.isFinite(createdMs)) {
    return true; // undatable -> keep (cannot prove stale)
  }
  return createdMs >= startMs;
};

/**
 * Drop orders from a cached list that no longer belong to the current business
 * day before seeding them into state on launch. Without this, a relaunch on a
 * new day briefly shows (and, while offline, keeps showing) the previous day's
 * cached orders before the first fetch reconciles them away. `pending_sync`
 * optimistic locals are always kept; everything else must be inside the current
 * business day (today's orders of ANY status, plus upcoming pre-orders).
 *
 * @param {Array<object>} cachedOrders - the list read from cache.
 * @param {Date|number} [now] - reference instant (defaults to current time).
 * @returns {Array<object>} the cache pruned to the current business day.
 */
export const pruneStaleCachedOrders = (cachedOrders, now = new Date()) => {
  const list = Array.isArray(cachedOrders) ? cachedOrders : [];
  const start = getBusinessDayStart(now);
  return list.filter((order) => {
    if (!order || !order._id) {
      return false;
    }
    if (order.status === 'pending_sync') {
      return true;
    }
    return isWithinCurrentBusinessDay(order, start);
  });
};

/**
 * True when an order is still operationally relevant (open / in progress /
 * scheduled pre-order) and therefore must be kept on screen even if a single
 * windowed server response happens not to include it. `pending_sync` is treated
 * as live here too (it is also preserved by {@link mergePendingOrders}).
 *
 * Losing a live order is the bug this whole module exists to prevent; dropping a
 * terminal one is correct house-keeping.
 *
 * @param {object} order
 * @returns {boolean}
 */
export const isLiveOrder = (order) => {
  if (!order || !order._id) {
    return false;
  }
  if (order.storno) {
    return false;
  }
  return !TERMINAL_STATUSES.has(order.status);
};

/** A canonical, server-assigned Mongo ObjectId (24 hex chars). */
const SERVER_OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

/**
 * Whether an order carries a real, server-assigned `_id`. Optimistic locals
 * minted on-device before they round-trip use a non-ObjectId id (e.g. `tmp…`),
 * so this distinguishes an order the backend has definitely persisted from one
 * that has never reached the server.
 *
 * @param {*} id - an order `_id`.
 * @returns {boolean}
 */
export const isServerAssignedId = (id) =>
  typeof id === 'string' && SERVER_OBJECT_ID_RE.test(id);

/**
 * Whether a live local order the server response OMITTED is a provable ghost —
 * i.e. an order the backend must have returned if it were still open, so its
 * absence proves it was paid/closed/deleted server-side and the local copy is
 * stale.
 *
 * Only used in the {@link reconcileOrderLists} `authoritative` mode (a deliberate
 * full sync on boot / foreground / entering the table map — NOT the 10s poll,
 * where a single windowed response transiently omitting an order must NOT drop
 * it). The proof is intentionally conservative — we purge ONLY when all hold:
 *  - the `_id` is server-assigned (a still-unsynced optimistic local is never a
 *    ghost — the server simply hasn't seen it yet);
 *  - it has no current/future `preOrder` (a scheduled order may legitimately sit
 *    outside the returned window);
 *  - it has a real `createdAt` that falls inside today's ALREADY-ELAPSED window
 *    (`businessDayStart … now`), which the server's today-only endpoint is
 *    guaranteed to cover — so a genuinely still-open order would have come back.
 * Undatable orders, future pre-orders and pending_sync locals are always kept.
 *
 * Purging is self-healing either way: a genuinely-open order wrongly dropped on
 * one full sync is re-added by the very next poll that includes it, whereas a
 * true ghost never returns from the server and stays gone.
 *
 * @param {object} order - a live local order absent from the server response.
 * @param {Date} businessDayStart - start of the current business day.
 * @param {number} nowMs - current epoch ms (upper bound of the elapsed window).
 * @returns {boolean} true when the order is a provable ghost and may be purged.
 */
export const isProvableServerGhost = (order, businessDayStart, nowMs) => {
  if (!order || !isServerAssignedId(order._id)) {
    return false; // never synced -> the server legitimately doesn't know it yet
  }
  const preOrderMs = order.preOrder ? new Date(order.preOrder).getTime() : NaN;
  if (Number.isFinite(preOrderMs) && preOrderMs > nowMs) {
    return false; // scheduled for later -> may sit outside the returned window
  }
  const createdMs = order.createdAt ? new Date(order.createdAt).getTime() : NaN;
  if (!Number.isFinite(createdMs)) {
    return false; // undatable -> cannot prove the window should have contained it
  }
  const startMs = businessDayStart instanceof Date
    ? businessDayStart.getTime()
    : Number(businessDayStart);
  if (!Number.isFinite(startMs)) {
    return false; // no usable boundary -> cannot prove stale
  }
  return createdMs >= startMs && createdMs <= nowMs;
};

/**
 * Reconcile a freshly-fetched server order list with the orders already held in
 * memory WITHOUT losing any live order.
 *
 * The backend order endpoint only returns *today's* orders (a 3am–3am window)
 * for the *currently selected place*. Blindly replacing local state with that
 * response — the previous behaviour — silently dropped any still-open order or
 * future pre-order the narrow query omitted (and, because the result is also
 * cached, the order stayed invisible across an app restart even though it was
 * still in the database). This is exactly the "order vanished from the list and
 * never came back" failure.
 *
 * Reconciliation rules:
 *  - The server is authoritative for every order it returns (it wins on every
 *    field, ordering and presence).
 *  - A local order absent from the server response is KEPT when it is still live
 *    (`pending_sync`, open, in-progress, scheduled pre-order) AND still belongs
 *    to the current business day (see {@link isWithinCurrentBusinessDay}). It is
 *    dropped when it is terminal (closed/cancelled/storno) — those may correctly
 *    age out of the window — OR when it provably predates today's window with no
 *    upcoming pre-order. The day bound is essential: the server only ever returns
 *    the current business day, so a previous-day order stuck as "open" in the
 *    cache could never come back to be aged out, and was resurrected on every
 *    launch — making the operator see yesterday's orders instead of today's.
 *
 * Net effect: a live order can never be lost to a narrow/transient query within
 * the current business day, while finished and previous-day orders still drain
 * away naturally. Server-returned orders are placed first (preserving the
 * backend's ordering), then still-pending optimistic locals, then any other
 * retained live locals.
 *
 * @param {Array<object>} serverOrders - orders returned by the API.
 * @param {Array<object>} currentOrders - current in-memory orders.
 * @param {Date|number} [now] - reference instant for the business-day bound.
 * @param {{
 *   authoritative?: boolean,
 *   confirmedGhostIds?: Set<string>,
 *   protectedIds?: Set<string>,
 *   preferLocalIds?: Set<string>,
 * }} [options] - when `authoritative` is true the
 *   server response is treated as a COMPLETE view of today's orders, so a live
 *   local order the server omits that is a provable ghost (see
 *   {@link isProvableServerGhost}) is PURGED instead of retained. Pass this only
 *   on a deliberate full sync (boot / foreground / opening the table map).
 *   On a routine poll a ghost is purged only once it is in `confirmedGhostIds`
 *   (absent from several consecutive responses, see {@link trackServerMisses}),
 *   so one transiently-incomplete response can never drop a still-open order.
 *   `protectedIds` (orders this device just created/received) are never purged.
 *   `preferLocalIds` (orders updated locally AFTER this fetch was sent) keep
 *   their local copy instead of the older server snapshot; the caller refetches.
 * @returns {Array<object>} reconciled list that preserves every live order.
 */
export const reconcileOrderLists = (serverOrders, currentOrders, now = new Date(), options = {}) => {
  const safeServer = Array.isArray(serverOrders) ? serverOrders.filter((o) => o && o._id) : [];
  const safeCurrent = Array.isArray(currentOrders) ? currentOrders : [];
  const serverOrderIds = new Set(safeServer.map((o) => o._id));
  const businessDayStart = getBusinessDayStart(now);
  const authoritative = options && options.authoritative === true;
  const confirmedGhostIds = options && options.confirmedGhostIds instanceof Set ? options.confirmedGhostIds : null;
  const protectedIds = options && options.protectedIds instanceof Set ? options.protectedIds : null;
  const preferLocalIds = options && options.preferLocalIds instanceof Set ? options.preferLocalIds : null;
  const nowMs = now instanceof Date ? now.getTime() : Number(now);

  // Index the current orders by id so we can REUSE the existing object reference
  // for any server order whose content is byte-identical to what we already hold.
  // The server response is a freshly-parsed array, so without this every poll
  // hands back all-new order objects — defeating React.memo on the order rows and
  // forcing every visible card to re-render on every 10s fetch even when nothing
  // about the order changed. Keeping the prior reference for unchanged orders
  // means only the orders that genuinely changed get a new identity (and re-render).
  // The server stays fully authoritative: the old object is reused ONLY when it is
  // deep-equal to the server's, so the displayed data is identical either way.
  const currentById = new Map();
  safeCurrent.forEach((order) => {
    if (order && order._id) {
      currentById.set(order._id, order);
    }
  });

  const reconciledServer = safeServer.map((serverOrder) => {
    const previous = currentById.get(serverOrder._id);
    if (previous && preferLocalIds && preferLocalIds.has(serverOrder._id)) {
      return previous; // changed locally after this snapshot was requested -> the snapshot is older
    }
    if (previous && previous !== serverOrder) {
      try {
        if (JSON.stringify(previous) === JSON.stringify(serverOrder)) {
          return previous; // unchanged — preserve the reference so the row won't re-render
        }
      } catch (_) {
        // Any serialization issue: fall back to the authoritative server object.
      }
    }
    return serverOrder;
  });

  // A dineIn table holds exactly ONE open tab. If the server already returns an
  // OPEN order for a table, any local order still held for that SAME table under
  // a different id has been superseded — it synced into the server's order
  // (possibly under a different canonical id when the backend de-duplicated a
  // device-minted id). Collapse it so it does not linger as a ghost/duplicate
  // "open" order on an already-occupied table. Keyed on an open server order, so
  // a table whose only server order is closed/absent still keeps its live local.
  const openServerTableIds = new Set(
    safeServer
      .filter((order) => order && order.tableId && isLiveOrder(order))
      .map((order) => String(order.tableId))
  );
  const isSupersededByOpenServerTable = (order) =>
    order && order.tableId && openServerTableIds.has(String(order.tableId));

  const retainedPending = [];
  const retainedLive = [];

  safeCurrent.forEach((order) => {
    if (!order || !order._id || serverOrderIds.has(order._id)) {
      return; // server is the source of truth for anything it returned
    }
    if (isSupersededByOpenServerTable(order)) {
      return; // the table already has an authoritative open order on the server
    }
    if (order.status === 'pending_sync') {
      retainedPending.push(order); // optimistic order not yet persisted
      return;
    }
    if (isLiveOrder(order) && isWithinCurrentBusinessDay(order, businessDayStart)) {
      const purgeEligible = (authoritative || (confirmedGhostIds && confirmedGhostIds.has(order._id)))
        && !(protectedIds && protectedIds.has(order._id));
      if (purgeEligible && isProvableServerGhost(order, businessDayStart, nowMs)) {
        return; // the server must have returned this today order -> it's a closed/deleted/moved ghost, purge
      }
      retainedLive.push(order); // open / scheduled order from today the window missed
    }
    // terminal, or previous-day, local order absent from the window -> let it age out
  });

  if (!retainedPending.length && !retainedLive.length) {
    return reconciledServer;
  }
  return [...retainedPending, ...reconciledServer, ...retainedLive];
};

/**
 * Consecutive successful order-list responses a server-assigned live local
 * order must be missing from before a routine poll may purge it as a ghost.
 * Two misses (~20s at the 10s floor) rule out one response that raced a write.
 */
export const GHOST_CONFIRM_MISSES = 2;

/**
 * Minimum time between two counted misses of the same order. Back-to-back
 * fetches (a queued refetch right after a poll) count once, so "confirmed"
 * always means absent across responses at least this far apart.
 */
export const GHOST_MISS_MIN_SPACING_MS = 5 * 1000;

/**
 * How long an order this device just created, edited or received over realtime
 * is shielded from the ghost purge. Covers the read that was already in flight
 * when the write landed, so a fresh order can never flicker out of the list.
 */
export const RECENT_TOUCH_GRACE_MS = 30 * 1000;

/**
 * Count, per order, how many consecutive server responses have omitted a live,
 * server-assigned local order. An id the server returned again (or that is no
 * longer live / held locally) drops out of the map, so the count only grows
 * while the order keeps being absent.
 *
 * This is what lets the routine poll converge on the server: before, only a
 * deliberate full sync (boot / foreground / table map) could purge an order that
 * was closed, merged, moved or deleted on another device, so a terminal that
 * stayed on one screen kept showing it — and two devices disagreed.
 *
 * An EMPTY server response never counts: while this device holds live orders
 * it is far more likely a wrong/partial answer than every order disappearing,
 * so the counts are carried over untouched.
 *
 * @param {Map<string, {count: number, at: number}>} previousMisses - state after the last response.
 * @param {Array<object>} serverOrders - orders the server just returned.
 * @param {Array<object>} currentOrders - orders held locally before reconciling.
 * @param {number} [now] - epoch ms of this response.
 * @param {number} [minSpacingMs] - see {@link GHOST_MISS_MIN_SPACING_MS}.
 * @returns {Map<string, {count: number, at: number}>} fresh miss state.
 */
export const trackServerMisses = (
  previousMisses, serverOrders, currentOrders, now = Date.now(), minSpacingMs = GHOST_MISS_MIN_SPACING_MS
) => {
  const previous = previousMisses instanceof Map ? previousMisses : new Map();
  const safeServer = (Array.isArray(serverOrders) ? serverOrders : []).filter((order) => order && order._id);
  if (!safeServer.length) {
    return new Map(previous);
  }
  const serverIds = new Set(safeServer.map((order) => order._id));
  const next = new Map();

  (Array.isArray(currentOrders) ? currentOrders : []).forEach((order) => {
    if (!order || !isServerAssignedId(order._id) || serverIds.has(order._id) || !isLiveOrder(order)) {
      return;
    }
    const prior = previous.get(order._id);
    if (prior && now - prior.at < minSpacingMs) {
      next.set(order._id, prior); // too soon after the last counted miss -> same miss
      return;
    }
    next.set(order._id, { count: (prior ? prior.count : 0) + 1, at: now });
  });

  return next;
};

/**
 * Ids whose miss count reached the confirmation threshold.
 *
 * @param {Map<string, {count: number, at: number}>} misses - output of {@link trackServerMisses}.
 * @param {number} [minMisses] - threshold (defaults to {@link GHOST_CONFIRM_MISSES}).
 * @returns {Set<string>} ids a routine poll may purge (subject to the ghost proof).
 */
export const selectConfirmedGhostIds = (misses, minMisses = GHOST_CONFIRM_MISSES) => {
  const confirmed = new Set();

  if (misses instanceof Map) {
    misses.forEach((entry, id) => {
      if (entry && entry.count >= minMisses) {
        confirmed.add(id);
      }
    });
  }

  return confirmed;
};

/**
 * Ids touched locally within `graceMs`, pruning older entries from the map in
 * place so it stays bounded.
 *
 * @param {Map<string, number>} touchedAt - order id -> epoch ms of the last local touch.
 * @param {number} now - current epoch ms.
 * @param {number} [graceMs] - shield window (defaults to {@link RECENT_TOUCH_GRACE_MS}).
 * @returns {Set<string>} ids touched within the window.
 */
export const collectRecentlyTouchedIds = (touchedAt, now, graceMs = RECENT_TOUCH_GRACE_MS) => {
  const recent = new Set();

  if (!(touchedAt instanceof Map)) {
    return recent;
  }
  touchedAt.forEach((at, id) => {
    if (now - at <= graceMs) {
      recent.add(id);
    } else {
      touchedAt.delete(id);
    }
  });

  return recent;
};

/**
 * Ids touched locally at or after `sinceMs` (i.e. after a fetch was sent): the
 * server snapshot of these is older than what the device already shows.
 *
 * @param {Map<string, number>} touchedAt - order id -> epoch ms of the last local touch.
 * @param {number} sinceMs - epoch ms the fetch started.
 * @returns {Set<string>} ids changed locally since `sinceMs`.
 */
export const collectTouchedSince = (touchedAt, sinceMs) => {
  const ids = new Set();

  if (touchedAt instanceof Map && Number.isFinite(sinceMs)) {
    touchedAt.forEach((at, id) => {
      if (at >= sinceMs) {
        ids.add(id);
      }
    });
  }

  return ids;
};

/**
 * Whether a realtime order payload is a slim change notice rather than a full
 * order. The backend relays order events to the other API workers/hosts as
 * `{documentId, status, …, __realtime: {slim: true}}` only; such a notice must update
 * what it carries and trigger a reload — never be inserted as a new order row.
 *
 * @param {object} payload - realtime order payload.
 * @returns {boolean}
 */
export const isSlimRealtimeNotice = (payload) =>
  Boolean(payload && typeof payload === 'object' && payload.__realtime && payload.__realtime.slim === true);

/**
 * How long a not-yet-echoed optimistic pairing is kept applied before it is
 * force-evicted. The backend back-fills the order snapshot on a successful PUT
 * (see food.controller.js `updateFoodPairing`), so a pairing normally confirms
 * within a poll or two; this bound only exists so a pairing the server never
 * echoes (order aged out of the window, line removed upstream, back-fill genuinely
 * failed) can neither leak in memory nor mask the real server state forever — it
 * simply steps aside and lets the server be authoritative again.
 */
export const PAIRING_OVERLAY_TTL_MS = 5 * 60 * 1000;

/**
 * TTL for an optimistic order-status overlay (see {@link applyStatusOverlay}).
 * Long enough to hide normal server + poll lag after an operator accepts/advances
 * an order, short enough that a status the backend never echoes cannot mask the
 * server forever.
 */
export const STATUS_OVERLAY_TTL_MS = 60 * 1000;

/**
 * TTL for the optimistic `closed` overlay set right after a table is paid on
 * this device. The payment reaches the backend through the offline queue, so it
 * can legitimately lag well past {@link STATUS_OVERLAY_TTL_MS} on a slow or
 * flaky connection; the table must stay freed until the server echoes `closed`.
 */
export const PAYMENT_CLOSE_OVERLAY_TTL_MS = 5 * 60 * 1000;

const toIdString = (value) => {
  if (!value) {
    return '';
  }
  if (typeof value === 'object') {
    return String(value.$oid || value._id || '');
  }

  return String(value);
};

const countLineUnits = (items) => (Array.isArray(items) ? items : []).reduce(
  (sum, item) => sum + (item ? Number(item.amount || item.quantity || 0) || 0 : 0),
  0
);

/**
 * Whether a payment settles the WHOLE open tab of a dineIn table (so the table
 * must be freed), as opposed to saving the tab, a proforma, or a split payment
 * that only covers some of its lines.
 *
 * The split flow pays the selected lines through the regular payment path
 * (selected lines in the `alreadySentCartItems` slot, the full tab as `order`),
 * so a split is recognised by paying fewer units than the existing tab holds.
 *
 * @param {object} params
 * @param {object} [params.currentTable] - the table being paid.
 * @param {boolean} [params.isExistingOrder] - the payment targets an existing tab.
 * @param {object} [params.existingOrder] - that tab (its `items` are the full tab).
 * @param {Array<object>} [params.paidLineItems] - every line sent with this payment.
 * @param {boolean} [params.isSavedForLater] - a save, not a payment.
 * @param {boolean} [params.proforma] - a proforma print, not a payment.
 * @param {Array<object>} [params.selectedItemsToPay] - explicit split selection.
 * @returns {boolean}
 */
export const isFullTableSettlement = ({
  currentTable,
  isExistingOrder,
  existingOrder,
  paidLineItems,
  isSavedForLater,
  proforma,
  selectedItemsToPay,
} = {}) => {
  if (!currentTable?._id || !isExistingOrder || isSavedForLater || proforma) {
    return false;
  }
  if (Array.isArray(selectedItemsToPay) && selectedItemsToPay.length > 0) {
    return false;
  }

  return countLineUnits(paidLineItems) >= countLineUnits(existingOrder?.items);
};

/**
 * Pick the live order(s) to flip to `closed` after a table was fully paid here.
 *
 * The paid order is matched by id first. When the id is not in the list (the
 * local copy is held under a different id than the one the payment resolved),
 * every live order on the paid table is taken instead — a dineIn table holds a
 * single open tab, so whatever still shows it as busy is the tab just settled.
 *
 * @param {Array<object>} orders - current (un-overlaid) order list.
 * @param {{orderId?: *, tableId?: *}} target - the paid order id and its table.
 * @returns {string[]} ids of the orders to overlay as `closed`.
 */
export const selectPaidTableOrderIds = (orders, { orderId, tableId } = {}) => {
  const list = Array.isArray(orders) ? orders.filter(isLiveOrder) : [];
  const paidId = toIdString(orderId);
  const paidTableId = toIdString(tableId);

  if (paidId) {
    const byId = list.filter((order) => toIdString(order._id) === paidId);
    if (byId.length) {
      return byId.map((order) => toIdString(order._id));
    }
  }

  if (!paidTableId) {
    return [];
  }

  return list
    .filter((order) => toIdString(order.tableId) === paidTableId)
    .map((order) => toIdString(order._id));
};

/**
 * Monotonic rank of the order-status lifecycle. Used by {@link applyStatusOverlay}
 * to decide when the server has "caught up" to (or moved past) an optimistic
 * status: the overlay is confirmed as soon as the server order's rank is >= the
 * optimistic rank. `busy` shares `ready`'s rank (both are post-preparing states).
 */
const STATUS_RANK = {
  pending: 0,
  accepted: 1,
  preparing: 2,
  ready: 3,
  busy: 3,
  closed: 4,
};

/**
 * Overlay not-yet-confirmed optimistic order-status changes onto an order list.
 *
 * When the operator accepts (or otherwise advances) an online order we flip the
 * status IN MEMORY immediately so the row moves to the accepted/right list at
 * once, and persist it in the background (see `setOrderStatusOptimistic` in
 * {@link ./OrdersContext.js}). Until the backend echoes the new status, a
 * server-authoritative poll/reconcile would overwrite the order with its still
 * `pending` version — the exact off-by-one where an accepted order only appears
 * on the NEXT successful refetch. Re-applying this overlay on every commit keeps
 * the new status visible until the server catches up.
 *
 * Confirmation is RANK-based: an entry is handed back to the server (and pruned)
 * once the server order's status rank is >= the optimistic rank, or the order is
 * stornoed/cancelled (server-terminal), or its status is unknown/non-pending — in
 * every case the server wins from that point. Entries older than
 * {@link STATUS_OVERLAY_TTL_MS} are evicted so a never-echoed status cannot leak.
 *
 * Pure and immutable: unchanged orders keep their existing reference (so
 * React.memo'd rows do not needlessly re-render), and the pending map is never
 * mutated here — confirmed/expired keys are returned for the caller to prune.
 *
 * @param {Array<object>} orders - order list about to be committed.
 * @param {Iterable<{orderId:string,status:string,savedAt?:number,ttlMs?:number}>|Map} pendingStatuses
 *   pending status entries (an array, or a Map whose values are the entries);
 *   `ttlMs` overrides {@link STATUS_OVERLAY_TTL_MS} for that entry.
 * @param {number} [now] - reference epoch ms for the TTL (defaults to Date.now()).
 * @returns {{ orders: Array<object>, confirmedKeys: string[] }} the overlaid list
 *   and the `orderId` keys the caller should prune (server-confirmed or expired).
 */
export const applyStatusOverlay = (orders, pendingStatuses, now = Date.now()) => {
  const list = Array.isArray(orders) ? orders : [];
  const entries = Array.isArray(pendingStatuses)
    ? pendingStatuses
    : pendingStatuses && typeof pendingStatuses.values === 'function'
      ? Array.from(pendingStatuses.values())
      : [];

  if (!entries.length || list.length === 0) {
    return { orders: list, confirmedKeys: [] };
  }

  const confirmedKeys = [];

  // Index live pending entries by order id; evict any past its TTL here so it is
  // neither applied nor scanned against the list.
  const byOrder = new Map();
  entries.forEach((entry) => {
    if (!entry || !entry.orderId || !entry.status) {
      return;
    }
    const ttlMs = Number.isFinite(entry.ttlMs) && entry.ttlMs > 0 ? entry.ttlMs : STATUS_OVERLAY_TTL_MS;
    if (Number.isFinite(entry.savedAt) && now - entry.savedAt > ttlMs) {
      confirmedKeys.push(String(entry.orderId));

      return;
    }
    byOrder.set(String(entry.orderId), entry);
  });

  const nextOrders = list.map((order) => {
    const entry = order && order._id ? byOrder.get(String(order._id)) : null;
    if (!entry) {
      return order;
    }

    const serverStatus = order.status;
    const serverRank = STATUS_RANK[serverStatus];
    const optimisticRank = STATUS_RANK[entry.status] ?? 0;

    // Server has reached (or moved past) the optimistic status, has terminated
    // the order (storno/cancelled), or reports an unknown non-pending status —
    // the server is authoritative from here on, so confirm + prune.
    // An optimistic `closed` (a table paid on this device) is only confirmed by a
    // terminal server state: a still-open `waitingForPayment` / `pending_sync`
    // tab has no rank and would otherwise hand the busy table straight back.
    const serverCaughtUp = entry.status === 'closed'
      ? order.storno === true || TERMINAL_STATUSES.has(serverStatus)
      : order.storno === true ||
        serverStatus === 'cancelled' ||
        (serverStatus !== 'pending' && serverRank === undefined) ||
        (serverRank !== undefined && serverRank >= optimisticRank);

    if (serverCaughtUp) {
      confirmedKeys.push(String(order._id));

      return order;
    }

    return { ...order, status: entry.status };
  });

  return { orders: nextOrders, confirmedKeys };
};

/**
 * Stable identity of whatever an order line's `item` currently points at. The
 * backend stores the pairing as `item.item = <food _id>` (an ObjectId), which the
 * order-list read may return raw or populated; either way this yields the food id
 * as a string. Empty string when the line is unpaired.
 *
 * @param {*} itemItem - an order line's `item` field.
 * @returns {string}
 */
const pairedFoodIdOf = (itemItem) => {
  if (!itemItem) {
    return '';
  }
  if (typeof itemItem === 'object') {
    return String(itemItem._id ?? itemItem.foodId ?? '');
  }
  return String(itemItem);
};

/**
 * The ids that mean "the server now reflects THIS pending pairing". Primarily the
 * paired food's `_id` (what the backend writes to `item.item`); the size id is
 * also accepted so an already-overlaid value can never be mistaken for unconfirmed.
 *
 * @param {{foodId?:string, sizeId?:string, food?:object}} entry
 * @returns {string[]}
 */
const matchIdsOf = (entry) => {
  const ids = [
    entry?.foodId ?? entry?.food?.foodId ?? entry?.food?._id,
    entry?.sizeId ?? entry?.food?.size?._id,
  ]
    .filter(Boolean)
    .map(String);

  return Array.from(new Set(ids));
};

/**
 * Overlay not-yet-confirmed optimistic food pairings onto an order list.
 *
 * When the operator pairs an online-order line (Foodora/Wolt/Webshop) to a local
 * product we apply the pairing IN MEMORY immediately and persist it in the
 * background (see `pairOrderItem` in {@link ./OrdersContext.js}). Until the
 * backend echoes the pairing back, a server-authoritative reconcile
 * ({@link reconcileOrderLists}) would overwrite the order with a version whose
 * line is still unpaired — flashing the row back to "Nincs párosítva". Re-applying
 * this overlay on every commit keeps the pairing visible until the server catches
 * up.
 *
 * Confirmation is MATCH-based, not truthiness-based: the pairing is handed back to
 * the server (and pruned) only once the server line points at the SAME food we
 * paired. This is what lets re-pairing an already-paired line (Food A -> Food B)
 * get the exact same protection as a first-time pairing — the stale Food A on the
 * line does not count as confirmation of the pending Food B. Entries older than
 * {@link PAIRING_OVERLAY_TTL_MS} are evicted so a never-echoed pairing cannot leak
 * or mask the server forever.
 *
 * Pure and immutable: unchanged orders/items keep their existing reference (so
 * React.memo'd rows do not needlessly re-render), and the pending map is never
 * mutated here — confirmed/expired keys are returned for the caller to prune.
 *
 * @param {Array<object>} orders - order list about to be committed.
 * @param {Iterable<{orderId:string,itemId:string,food:object,foodId?:string,sizeId?:string,savedAt?:number}>|Map} pendingPairings
 *   pending pairing entries (an array, or a Map whose values are the entries).
 * @param {number} [now] - reference epoch ms for the TTL (defaults to Date.now()).
 * @returns {{ orders: Array<object>, confirmedKeys: string[] }} the overlaid list
 *   and the `${orderId}:${itemId}` keys the caller should prune (server-confirmed
 *   or TTL-expired).
 */
export const applyPairingOverlay = (orders, pendingPairings, now = Date.now()) => {
  const list = Array.isArray(orders) ? orders : [];
  const entries = Array.isArray(pendingPairings)
    ? pendingPairings
    : pendingPairings && typeof pendingPairings.values === 'function'
      ? Array.from(pendingPairings.values())
      : [];

  if (!entries.length || list.length === 0) {
    return { orders: list, confirmedKeys: [] };
  }

  const confirmedKeys = [];

  // Group live pending entries by order id so the list is walked once. Any entry
  // past its TTL is evicted here rather than grouped, so it is neither applied nor
  // scanned against the list.
  const byOrder = new Map();
  entries.forEach((entry) => {
    if (!entry || !entry.orderId || !entry.itemId) {
      return;
    }
    if (Number.isFinite(entry.savedAt) && now - entry.savedAt > PAIRING_OVERLAY_TTL_MS) {
      confirmedKeys.push(`${entry.orderId}:${entry.itemId}`);

      return;
    }
    const orderId = String(entry.orderId);
    if (!byOrder.has(orderId)) {
      byOrder.set(orderId, []);
    }
    byOrder.get(orderId).push(entry);
  });

  const nextOrders = list.map((order) => {
    const group = order && order._id ? byOrder.get(String(order._id)) : null;
    if (!group || !group.length || !Array.isArray(order.items)) {
      return order;
    }

    let orderChanged = false;
    const nextItems = order.items.map((item) => {
      let nextItem = item;

      const itemEntry = group.find((entry) => entry.itemId === String(item?._id));
      if (itemEntry) {
        const matchIds = matchIdsOf(itemEntry);
        if (matchIds.includes(pairedFoodIdOf(item?.item))) {
          // Server line now points at the food we paired — it wins from here on.
          confirmedKeys.push(`${itemEntry.orderId}:${itemEntry.itemId}`);
        } else if (itemEntry.food) {
          nextItem = { ...item, item: itemEntry.food };
          orderChanged = true;
        }
      }

      if (Array.isArray(nextItem.extras) && nextItem.extras.length) {
        let extrasChanged = false;
        const nextExtras = nextItem.extras.map((extra) => {
          const extraEntry = group.find((entry) => entry.itemId === String(extra?._id));
          if (!extraEntry) {
            return extra;
          }
          const matchIds = matchIdsOf(extraEntry);
          if (matchIds.includes(pairedFoodIdOf(extra?.item))) {
            confirmedKeys.push(`${extraEntry.orderId}:${extraEntry.itemId}`);

            return extra;
          }
          if (!extraEntry.food) {
            return extra;
          }
          extrasChanged = true;

          return { ...extra, item: extraEntry.food };
        });

        if (extrasChanged) {
          nextItem = { ...nextItem, extras: nextExtras };
          orderChanged = true;
        }
      }

      return nextItem;
    });

    return orderChanged ? { ...order, items: nextItems } : order;
  });

  return { orders: nextOrders, confirmedKeys };
};

/**
 * Build a non-destructive patch from a normalized incoming order, keeping only
 * the fields that were genuinely present on the RAW realtime payload.
 *
 * Realtime `order:modify`/`order:pay` events frequently carry a partial order
 * (only the changed fields). The normalizer expands a partial order to the full
 * key set, defaulting missing fields to `undefined` / `[]`. Merging that
 * straight over an existing order would wipe good data — e.g. blanking
 * `restaurantPlace` so the order falls out of the place filter and disappears.
 * By only patching keys that exist on the raw payload, an existing order keeps
 * every field the event did not explicitly change.
 *
 * @param {object} rawIncoming - the raw order payload as received.
 * @param {object} normalizedIncoming - the same order after property filtering.
 * @returns {object} a patch containing only the explicitly-present fields.
 */
export const buildOrderPatch = (rawIncoming, normalizedIncoming) => {
  const raw = rawIncoming && typeof rawIncoming === 'object' ? rawIncoming : {};
  const normalized = normalizedIncoming && typeof normalizedIncoming === 'object' ? normalizedIncoming : {};
  const patch = {};
  Object.keys(normalized).forEach((key) => {
    // `_id` always travels; otherwise only carry a field the event actually sent.
    if (key === '_id' || raw[key] !== undefined) {
      patch[key] = normalized[key];
    }
  });
  return patch;
};

/**
 * Whether an order carries the fields required to be placed and displayed in the
 * operator's lists (its place and type). A realtime payload that creates a
 * brand-new order without these cannot be rendered correctly, so the caller
 * should reconcile against the API rather than insert a half-order.
 *
 * @param {object} order
 * @returns {boolean}
 */
export const hasDisplayableOrderShape = (order) =>
  Boolean(order && order._id && order.restaurantPlace && order.orderType);

/**
 * Normalize an order's restaurant-place reference to a comparable id string.
 * `restaurantPlace` may arrive as a raw ObjectId string, or as a populated
 * object (`{ _id }` / `{ id }`), depending on the endpoint / realtime payload.
 *
 * @param {object} order
 * @returns {string|null} the place id as a string, or null when absent.
 */
export const getOrderPlaceId = (order) => {
  const place = order && typeof order === 'object' ? order.restaurantPlace : null;
  if (!place) {
    return null;
  }
  if (typeof place === 'object') {
    const id = place._id || place.id;
    return id ? id.toString() : null;
  }
  return place.toString();
};

/**
 * Whether an order belongs to the currently-selected restaurant place.
 *
 * This is the guard that stops a SIBLING place's order (Foodora/Netpincér or
 * otherwise) from leaking into this terminal. The backend realtime channel is
 * scoped per restaurant, not per place, so a terminal viewing Place A still
 * receives order events for Place B of the same restaurant. We only ever keep /
 * act on orders for the active place.
 *
 * Fail-open by design: if the current place is unknown, or the order carries no
 * place at all, we DO NOT drop it — we only reject an order whose place is known
 * and explicitly differs. This preserves the "never lose an order" guarantee for
 * legitimate orders that lack a populated place.
 *
 * @param {object} order
 * @param {string|null} currentPlaceId
 * @returns {boolean}
 */
export const orderBelongsToPlace = (order, currentPlaceId) => {
  if (!currentPlaceId) {
    return true;
  }
  const orderPlaceId = getOrderPlaceId(order);
  if (!orderPlaceId) {
    return true;
  }
  return orderPlaceId === currentPlaceId.toString();
};
